import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useOptionalWorkspace } from '../lib/workspace';
import { useWorkspaceQuery } from '../hooks/useWorkspaceQuery';
import { queryKeys } from '../lib/query-keys';
import {
  convertAlert,
  dismissAlert,
  getAlert,
  listAlertForwards,
  markAlertRead,
  revokeAlertForward,
  snoozeAlert,
  type AlertForwardSummary,
  type AlertListItem,
} from '../lib/api';
import {
  Alert,
  Button,
  Card,
  CategoryIcon,
  PageHeader,
  SeverityBadge,
  Spinner,
  WorkspaceTag,
  alertCategoryLabel,
} from '@aigovops/design-system';
import { fmtDate, daysUntil } from '../lib/format';
import { SnoozeDialog } from '../components/alerts/SnoozeDialog';
import { ConfirmDialog } from '../components/alerts/ConfirmDialog';
import { roster } from '../data/editors';

function parseTldr(json: string): string[] {
  try {
    const v = JSON.parse(json) as unknown;
    return Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : [];
  } catch {
    return [];
  }
}

function parseRefs(json: string): string[] {
  try {
    const v = JSON.parse(json) as unknown;
    return Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : [];
  } catch {
    return [];
  }
}

export function AlertDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const workspaceCtx = useOptionalWorkspace();
  const slug = workspaceCtx?.workspace.slug ?? null;
  const prefix = slug ? `/w/${slug}/app` : '/app'; // scope-check-ignore: legacy fallback
  // Back-nav honours where the user came from (nav-ia-v2 §9.4 F13): a
  // consultant who drilled in from the cross-workspace inbox must land
  // back there, not in this one client's inbox — otherwise the sweep
  // they were halfway through is gone and they restart it.
  const [detailParams] = useSearchParams();
  const fromAggregate = detailParams.get('from') === 'aggregate';
  const returnSearch = detailParams.get('return');
  const backTo = fromAggregate
    ? `${prefix}/alerts?${returnSearch ? decodeURIComponent(returnSearch) : 'view=aggregate'}`
    : `${prefix}/alerts`;
  const canConvert =
    workspaceCtx?.role === 'owner' || workspaceCtx?.role === 'admin' || workspaceCtx?.role === 'editor';
  // Same role set as convert today, named separately on purpose:
  // forwarding privileged material outside the tenant and converting an
  // alert into register rows are different capabilities, and a future
  // widening of one must not silently widen the other.
  const canForward = canConvert;

  const alertState = useWorkspaceQuery(
    (s) => queryKeys.alertDetail(s, id ?? ''),
    (s) => id ? getAlert(s, id) : Promise.reject(new Error('missing alert id')),
  );
  const [local, setLocal] = useState<AlertListItem | null>(null);
  useEffect(() => {
    setLocal(alertState.data?.alert ?? null);
  }, [alertState.data]);

  // Opening the detail page IS the "read" action — mirrors the mockup's
  // cold-open story (Priya taps the row, it's read). Fire-and-forget;
  // failure just means the unread dot lingers, not worth surfacing.
  useEffect(() => {
    if (slug && id && local && !local.read_at) {
      markAlertRead(slug, id)
        .then((res) => setLocal(res.alert))
        .catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug, id, local?.alert_id]);

  const [snoozeOpen, setSnoozeOpen] = useState(false);
  const [confirmKind, setConfirmKind] = useState<'convert' | 'dismiss' | null>(null);
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [revokingId, setRevokingId] = useState<string | null>(null);
  const [revokeTarget, setRevokeTarget] = useState<AlertForwardSummary | null>(null);
  const [forwardAnnounce, setForwardAnnounce] = useState('');

  // Chain of custody. Any active member can read it — the point of the
  // panel is that the workspace can see what left it, not just whoever
  // sent it. Loaded alongside the alert rather than behind a disclosure
  // so "who has this?" is answerable without a click.
  const forwardsState = useWorkspaceQuery(
    (s) => queryKeys.alertForwards(s, id ?? ''),
    (s) => id ? listAlertForwards(s, id) : Promise.reject(new Error('missing alert id')),
  );
  const forwards = forwardsState.data?.forwards ?? [];

  async function handleRevoke() {
    const target = revokeTarget;
    if (!slug || !id || !target) return;
    setRevokingId(target.forward_id);
    setActionError(null);
    try {
      await revokeAlertForward(slug, id, target.forward_id);
      setRevokeTarget(null);
      // The row's Revoke button unmounts on refetch, so without an
      // announcement a screen-reader user gets no confirmation that the
      // one irreversible action on this page succeeded.
      setForwardAnnounce(`${target.recipient_email}'s link was revoked.`);
      forwardsState.refetch();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to revoke the link.');
    } finally {
      setRevokingId(null);
    }
  }

  function openSnooze() {
    setActionError(null);
    setSnoozeOpen(true);
  }
  function openConfirm(kind: 'convert' | 'dismiss') {
    setActionError(null);
    setConfirmKind(kind);
  }

  const tldr = useMemo(() => (local ? parseTldr(local.tldr_json) : []), [local]);
  const refs = useMemo(() => (local ? parseRefs(local.register_item_refs) : []), [local]);

  async function handleConvert() {
    if (!slug || !id) return;
    setBusy(true);
    setActionError(null);
    try {
      const result = await convertAlert(slug, id);
      if (result.status === 'refused') {
        setActionError(`Convert refused: ${result.reason ?? 'unknown reason'}.`);
      } else {
        setConfirmKind(null);
        alertState.refetch();
      }
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to convert.');
    } finally {
      setBusy(false);
    }
  }

  async function handleDismiss() {
    if (!slug || !id) return;
    setBusy(true);
    setActionError(null);
    try {
      const res = await dismissAlert(slug, id);
      setConfirmKind(null);
      setLocal(res.alert);
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to dismiss.');
    } finally {
      setBusy(false);
    }
  }

  async function handleSnooze(until: Date) {
    if (!slug || !id) return;
    setBusy(true);
    setActionError(null);
    try {
      const res = await snoozeAlert(slug, id, until.toISOString());
      setSnoozeOpen(false);
      setLocal(res.alert);
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to snooze.');
    } finally {
      setBusy(false);
    }
  }


  if (!workspaceCtx || (alertState.loading && local === null)) return <Spinner label="Loading alert" />;
  if (alertState.error) return <Alert variant="error">{alertState.error}</Alert>;
  if (!local) return <Alert variant="error">Alert not found.</Alert>;

  return (
    <div style={{ display: 'grid', gap: 'var(--space-4)', maxWidth: '48rem' }}>
      <Link
        to={backTo}
        style={{ fontSize: 'var(--font-size-2xs)', color: 'var(--secondary)', textDecoration: 'none' }}
      >
        {fromAggregate ? '← Back to all workspaces' : '← Back to alerts'}
      </Link>
      {/* Names the client whose alert this is when the user arrived
          from the cross-workspace inbox — the one context they don't
          carry in their head, and the one that decides whether the
          convert/forward actions below are safe to take. Read from the
          alert record, not route context (PR-P3-4 / P3-3-followup-2). */}
      {fromAggregate && local.workspace_name ? (
        <WorkspaceTag name={local.workspace_name} slug={local.workspace_slug} />
      ) : null}

      <PageHeader
        title={local.title}
        subtitle={
          <span style={{ display: 'inline-flex', gap: 'var(--space-2)', alignItems: 'center' }}>
            <CategoryIcon category={local.category} />
            <span>{alertCategoryLabel(local.category)}</span>
            <span aria-hidden="true">·</span>
            <span>{fmtDate(local.created_at)}</span>
            {local.deadline_at ? (
              <>
                <span aria-hidden="true">·</span>
                <span style={{ color: 'var(--warning)' }}>Deadline {fmtDate(local.deadline_at)}</span>
              </>
            ) : null}
          </span>
        }
        freshness={{
          updatedAt: new Date(local.updated_at).toISOString(),
          source: alertCategoryLabel(local.category),
          reviewer: roster,
          sourceHref: local.source_url ?? undefined,
        }}
        right={
          <div style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'center' }}>
            <SeverityBadge severity={local.severity === 'info' ? 'standard' : local.severity} />
            <span
              style={{
                fontSize: 'var(--font-size-3xs)',
                textTransform: 'uppercase',
                letterSpacing: '0.04em',
                color: 'var(--text-muted)',
              }}
            >
              {local.status}
            </span>
          </div>
        }
      />

      {actionError ? <Alert variant="error" onDismiss={() => setActionError(null)}>{actionError}</Alert> : null}

      {/* Recommended action renders FIRST — Priya's primary read, per
          UC-4 R2 B1 ("reco-band promoted above What Changed"). */}
      {local.recommended_action ? (
        <div
          style={{
            border: '1px solid var(--warning-fg)',
            borderRadius: 'var(--radius-card)',
            background: 'var(--warning-bg)',
            padding: 'var(--space-4)',
            fontFamily: 'var(--font-ui)',
          }}
        >
          <div style={{ fontSize: 'var(--font-size-3xs)', color: 'var(--warning-fg)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '4px' }}>
            Recommended action
          </div>
          <p style={{ margin: 0, color: 'var(--text)', fontWeight: 600, fontSize: 'var(--font-size-2xs)' }}>
            {local.recommended_action}
          </p>
        </div>
      ) : null}

      {tldr.length > 0 ? (
        <Card title="What changed">
          <ul style={{ listStyle: 'disc', margin: 0, paddingLeft: 'var(--space-4)', display: 'grid', gap: 'var(--space-1)' }}>
            {tldr.map((line, i) => (
              <li key={i} style={{ color: 'var(--text)', fontSize: 'var(--font-size-2xs)' }}>
                {line}
              </li>
            ))}
          </ul>
          {local.body ? (
            <details>
              <summary style={{ cursor: 'pointer', color: 'var(--secondary)', fontSize: 'var(--font-size-3xs)' }}>
                Read the full analysis
              </summary>
              <p style={{ margin: 'var(--space-2) 0 0', color: 'var(--text-muted)', fontSize: 'var(--font-size-2xs)', whiteSpace: 'pre-wrap' }}>
                {local.body}
              </p>
              {local.source_url ? (
                <p style={{ margin: 'var(--space-2) 0 0' }}>
                  <a href={local.source_url} target="_blank" rel="noreferrer" style={{ color: 'var(--secondary)' }}>
                    Source ↗
                  </a>
                </p>
              ) : null}
            </details>
          ) : null}
        </Card>
      ) : null}

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-2)' }}>
        {canConvert && local.status === 'open' ? (
          <Button
            variant="primary"
            onClick={() => openConfirm('convert')}
            disabled={busy || refs.length === 0}
            title={refs.length === 0 ? 'No matched register items yet — nothing to convert.' : undefined}
          >
            Convert to register items →
          </Button>
        ) : null}
        <Button variant="secondary" onClick={openSnooze} disabled={busy}>
          {local.user_snoozed_until ? `Snoozed until ${fmtDate(local.user_snoozed_until)}` : 'Snooze'}
        </Button>
        {canConvert && local.status !== 'closed' ? (
          <Button variant="secondary" onClick={() => openConfirm('dismiss')} disabled={busy}>
            Dismiss
          </Button>
        ) : null}
        {canForward ? (
          <Button
            variant="secondary"
            disabled={busy}
            onClick={() =>
              navigate(
                `${prefix}/alerts/${local.alert_id}/forward${fromAggregate ? '?from=aggregate' : ''}`
              )
            }
          >
            Forward outside the workspace →
          </Button>
        ) : null}
      </div>

      {refs.length > 0 ? (
        <Card title={`Impact — ${refs.length} register item${refs.length === 1 ? '' : 's'}`}>
          <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: '4px' }}>
            {refs.map((riskItemId) => (
              <li key={riskItemId}>
                <Link
                  to={`${prefix}/register/${riskItemId}?from=alert-${local.alert_id}`}
                  style={{ color: 'var(--secondary)', fontSize: 'var(--font-size-2xs)', textDecoration: 'none' }}
                >
                  Open {riskItemId} in Register →
                </Link>
              </li>
            ))}
          </ul>
        </Card>
      ) : null}

      <Card title={forwardsPanelTitle(forwards, forwardsState.loading, Boolean(forwardsState.error))}>
        {forwardsState.error ? (
          <Alert variant="warning">
            Couldn't load the forwarding record — this does NOT mean nothing was forwarded.{' '}
            <Button variant="ghost" compact onClick={() => { forwardsState.refetch(); }}>
              Retry
            </Button>
          </Alert>
        ) : forwardsState.loading ? (
          <Spinner label="Loading forwarding record" />
        ) : forwards.length === 0 ? (
          <span style={{ color: 'var(--text-muted)', fontSize: 'var(--font-size-2xs)' }}>
            Not forwarded outside this workspace.
          </span>
        ) : (
          <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: 'var(--space-3)' }}>
            {forwards.map((f) => (
              <li key={f.forward_id} style={{ display: 'grid', gap: 'var(--space-1)' }}>
                <span
                  style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 'var(--space-2)',
                    alignItems: 'baseline',
                  }}
                >
                  <span style={{ color: FORWARD_STATE_META[f.state].color, fontSize: 'var(--font-size-3xs)', fontWeight: 600 }}>
                    {FORWARD_STATE_META[f.state].glyph} {FORWARD_STATE_META[f.state].label}
                  </span>
                  <span style={{ color: 'var(--text)', fontSize: 'var(--font-size-2xs)' }}>
                    {f.recipient_email}
                  </span>
                </span>
                <span style={{ color: 'var(--text-muted)', fontSize: 'var(--font-size-3xs)' }}>
                  {forwardStateLine(f)}
                </span>
                {f.note ? (
                  <details>
                    <summary style={{ cursor: 'pointer', color: 'var(--secondary)', fontSize: 'var(--font-size-3xs)' }}>
                      Note sent
                    </summary>
                    <p style={{ margin: 'var(--space-1) 0 0', color: 'var(--text-muted)', fontSize: 'var(--font-size-3xs)', whiteSpace: 'pre-wrap' }}>
                      {f.note}
                    </p>
                  </details>
                ) : null}
                {canForward && f.state !== 'revoked' && f.state !== 'expired' ? (
                  <span>
                    <Button
                      variant="secondary"
                      compact
                      onClick={() => setRevokeTarget(f)}
                      disabled={revokingId === f.forward_id}
                      aria-label={`Revoke link for ${f.recipient_email}`}
                    >
                      {revokingId === f.forward_id ? 'Revoking…' : 'Revoke link'}
                    </Button>
                  </span>
                ) : null}
              </li>
            ))}
          </ul>
        )}
        <span aria-live="polite" className="sr-only">
          {forwardAnnounce}
        </span>
      </Card>

      <SnoozeDialog open={snoozeOpen} onClose={() => setSnoozeOpen(false)} onConfirm={handleSnooze} busy={busy} error={actionError} />
      <ConfirmDialog
        open={confirmKind === 'convert'}
        title="Convert to register items?"
        description={`Links ${refs.length} matched register item${refs.length === 1 ? '' : 's'} and marks this alert converted.`}
        confirmLabel="Convert →"
        onConfirm={handleConvert}
        onClose={() => setConfirmKind(null)}
        busy={busy}
        error={actionError}
      />
      <ConfirmDialog
        open={revokeTarget !== null}
        title={revokeTarget ? `Revoke ${revokeTarget.recipient_email}'s link?` : 'Revoke link?'}
        description={
          revokeTarget
            ? `They lose access immediately and will see "This link was revoked" if they open it. ` +
              `This can't be undone — you'd have to send a new link. ` +
              (revokeTarget.open_count > 0
                ? `Opened ${revokeTarget.open_count}× so far.`
                : 'Not opened yet.')
            : ''
        }
        confirmLabel="Revoke link"
        onConfirm={handleRevoke}
        onClose={() => setRevokeTarget(null)}
        busy={revokingId !== null}
        error={actionError}
        danger
      />
      <ConfirmDialog
        open={confirmKind === 'dismiss'}
        title="Dismiss this alert?"
        description="Moves it to Closed. This doesn't affect any linked register items."
        confirmLabel="Dismiss"
        onConfirm={handleDismiss}
        onClose={() => setConfirmKind(null)}
        busy={busy}
        error={actionError}
        danger
      />
    </div>
  );
}

/**
 * One line describing where a forwarded packet stands. Written out
 * rather than shown as a status chip because the interesting part is
 * usually the timing ("opened 3 times, last Aug 28"), and a chip that
 * says "opened" hides exactly that.
 */
function forwardStateLine(f: AlertForwardSummary): string {
  const sentBy = f.created_by_display_name ? ` by ${f.created_by_display_name}` : '';
  const sent = `Sent ${fmtDate(f.created_at)}${sentBy}`;
  // "Did they open it before I killed the link?" is the ONLY question
  // that matters at the moment of a wrong-address revoke, and this
  // branch used to be the one that dropped it.
  const opens =
    f.open_count > 0
      ? `opened ${f.open_count}×${f.last_opened_at ? `, last ${fmtDate(f.last_opened_at)}` : ''}`
      : 'never opened';

  if (f.state === 'revoked') {
    return `${sent} · ${opens} · revoked${f.revoked_at ? ` ${fmtDate(f.revoked_at)}` : ''}`;
  }
  if (f.state === 'expired') {
    return `${sent} · ${opens} · expired ${fmtDate(f.expires_at)}`;
  }
  const expiresIn = daysUntil(f.expires_at);
  const expiry =
    expiresIn !== null && expiresIn >= 0
      ? `expires in ${expiresIn} day${expiresIn === 1 ? '' : 's'}`
      : `expires ${fmtDate(f.expires_at)}`;
  if (f.state === 'opened') {
    return `${sent} · ${opens} · ${expiry}`;
  }
  // Never claim the sender relayed the link — nothing records that they
  // did. State only the delivery outcome the system actually observed.
  const delivery =
    f.email_delivery === 'error'
      ? ' · email delivery failed'
      : f.email_delivery === 'not_configured'
        ? " · gacpro didn't send an email (email isn't set up)"
        : '';
  return `${sent} · not opened yet · ${expiry}${delivery}`;
}

/** State is pre-conscious; the line beneath carries the timing. Glyph +
 *  color together, per the DS non-color-status contract. */
const FORWARD_STATE_META: Record<
  AlertForwardSummary['state'],
  { glyph: string; label: string; color: string }
> = {
  active: { glyph: '●', label: 'Live', color: 'var(--success)' },
  opened: { glyph: '◉', label: 'Opened', color: 'var(--secondary)' },
  expired: { glyph: '○', label: 'Expired', color: 'var(--text-muted)' },
  revoked: { glyph: '⊘', label: 'Revoked', color: 'var(--text-muted)' },
};

/** The count that decays is the one Priya needs — after revoking two of
 *  three, "Forwarded to 3 recipients" is true and useless. */
function forwardsPanelTitle(
  forwards: AlertForwardSummary[],
  loading: boolean,
  errored: boolean
): string {
  if (loading || errored) return 'Forwarded to';
  if (forwards.length === 0) return 'Forwarded to';
  const live = forwards.filter((f) => f.state === 'active' || f.state === 'opened').length;
  const who = `${forwards.length} recipient${forwards.length === 1 ? '' : 's'}`;
  return live === 0 ? `Forwarded to ${who} · no live links` : `Forwarded to ${who} · ${live} live`;
}
